import { db } from '@/db/client';
import { updatePlayer } from '@/db/players';
import { addSessionEvent } from '@/db/session-events';
import type { Player, PlayerId, Session, SessionId, UserId } from '@/schemas';
import { createOperationId } from '../../operation-queue';
import { createSystemEvent } from '../../session-helpers';
import type { QueueDependencies } from '../queue-utils';

type JoinPlayerQueueParams = QueueDependencies & {
  sessionId: SessionId;
  player: Player;
  entryRound: number;
  userId: UserId | undefined;
  shouldPersistColor: boolean;
};

type RemovePlayerQueueParams = QueueDependencies & {
  sessionId: SessionId;
  playerId: PlayerId;
  previousPlayer: Session['players'][number];
  userId: UserId | undefined;
  timestamp: number;
};

type ReorderPlayersQueueParams = QueueDependencies & {
  sessionId: SessionId;
  playerIds: PlayerId[];
  previousOrder: PlayerId[];
  userId: UserId | undefined;
};

export const enqueueJoinPlayer = async ({
  operationQueue,
  set,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  player,
  entryRound,
  userId,
  shouldPersistColor,
}: JoinPlayerQueueParams): Promise<void> => {
  await operationQueue.enqueue<void>({
    id: createOperationId(),
    type: 'joinPlayer',
    dedupKey: `player:join:${sessionId}:${player.id}`,
    priority: 6,
    optimisticUpdate: () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }
        draft.activeSession.players.push({
          id: player.id,
          name: player.name,
          nickname: player.nickname,
          color: player.color,
          userId: player.userId,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          deletedAt: undefined,
        });
      });
    },
    rollback: async () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }
        draft.activeSession.players = draft.activeSession.players.filter(
          (candidate) => candidate.id !== player.id,
        );
      });
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      await db.transaction(
        'rw',
        db.playerEvents,
        db.players,
        db.sessionEvents,
        db.sessionMetadata,
        async () => {
          if (shouldPersistColor) {
            await updatePlayer(player.id, { color: player.color, userId });
          }

          await addSessionEvent({
            sessionId,
            type: 'PlayerJoined',
            userId,
            payload: {
              playerId: player.id,
              name: player.name,
              nickname: player.nickname,
              color: player.color,
              entryRound,
            },
          });
        },
      );

      await updateUndoRedoAvailability(sessionId);
      await refreshSessionFromEvents(sessionId);
    },
  });
};

export const enqueueRemovePlayer = async ({
  operationQueue,
  set,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  playerId,
  previousPlayer,
  userId,
  timestamp,
}: RemovePlayerQueueParams): Promise<void> => {
  await operationQueue.enqueue<void>({
    id: createOperationId(),
    type: 'removePlayer',
    dedupKey: `player:remove:${sessionId}:${playerId}`,
    priority: 6,
    optimisticUpdate: () => {
      set((draft) => {
        const targetPlayer = draft.activeSession?.players.find(
          (candidate) => candidate.id === playerId,
        );
        if (targetPlayer && !targetPlayer.deletedAt) {
          targetPlayer.deletedAt = timestamp;
          targetPlayer.updatedAt = timestamp;
        }
      });
    },
    rollback: async () => {
      set((draft) => {
        const targetPlayer = draft.activeSession?.players.find(
          (candidate) => candidate.id === playerId,
        );
        if (targetPlayer) {
          targetPlayer.deletedAt = previousPlayer.deletedAt;
          targetPlayer.updatedAt = previousPlayer.updatedAt;
        }
      });
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      await addSessionEvent(
        createSystemEvent(
          sessionId,
          'PlayerLeft',
          {
            playerId,
          },
          userId,
        ),
      );

      await updateUndoRedoAvailability(sessionId);
      await refreshSessionFromEvents(sessionId);
    },
  });
};

export const enqueueReorderPlayers = async ({
  operationQueue,
  set,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  playerIds,
  previousOrder,
  userId,
}: ReorderPlayersQueueParams): Promise<void> => {
  await operationQueue.enqueue<void>({
    id: createOperationId(),
    type: 'reorderPlayers',
    dedupKey: `player:reorder:${sessionId}`,
    priority: 6,
    optimisticUpdate: () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }

        // Create a map of player ID to player object
        const playerMap = new Map(draft.activeSession.players.map((player) => [player.id, player]));

        // Separate active and deleted players
        const activePlayers: typeof draft.activeSession.players = [];
        const deletedPlayers: typeof draft.activeSession.players = [];

        // Collect players in the new order (active players)
        for (const playerId of playerIds) {
          const player = playerMap.get(playerId);
          if (player && !player.deletedAt) {
            activePlayers.push(player);
          }
        }

        // Collect any deleted players that weren't in the new order
        for (const player of draft.activeSession.players) {
          if (player.deletedAt && !playerIds.includes(player.id)) {
            deletedPlayers.push(player);
          }
        }

        // Rebuild the players array
        draft.activeSession.players = [...activePlayers, ...deletedPlayers];
      });
    },
    rollback: async () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }

        // Restore previous order
        const playerMap = new Map(draft.activeSession.players.map((player) => [player.id, player]));

        const activePlayers: typeof draft.activeSession.players = [];
        const deletedPlayers: typeof draft.activeSession.players = [];

        // Restore active players in previous order
        for (const playerId of previousOrder) {
          const player = playerMap.get(playerId);
          if (player && !player.deletedAt) {
            activePlayers.push(player);
          }
        }

        // Keep deleted players
        for (const player of draft.activeSession.players) {
          if (player.deletedAt && !previousOrder.includes(player.id)) {
            deletedPlayers.push(player);
          }
        }

        draft.activeSession.players = [...activePlayers, ...deletedPlayers];
      });
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      await addSessionEvent(
        createSystemEvent(
          sessionId,
          'PlayerOrderChanged',
          {
            playerIds,
          },
          userId,
        ),
      );

      await updateUndoRedoAvailability(sessionId);
      await refreshSessionFromEvents(sessionId);
    },
  });
};
